import React, { useState, useEffect } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { usePipeline } from './context/PipelineContext';
import { useFeatures } from './context/FeaturesContext';
import { payrollTabVisible } from './utils/featureFlags';
import { canUseGlobalSearch, canAccessTenantTaxForms } from './utils/endpointAccess';
import { useTerms } from './context/TerminologyContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Chip,
  Card,
  Button,
  CircularProgress,
  Badge,
  ButtonBase,
} from '@mui/material';
import useResponsive from './hooks/useResponsive';
import {
  AccountCircle,
  Logout as LogoutIcon,
  Lock as ChangePasswordIcon,
  Person as ProfileIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  AccessTime as ClockIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  Schedule as TimeEntriesIcon,
  People as EmployeesIcon,
  Receipt as ExpensesIcon,
  Description as InvoicesIcon,
  Email as EmailIcon,
  History as AuditIcon,
  Store as AffiliatesIcon,
  AssessmentOutlined as HoursIcon,
  Assessment as ReportsIcon,
  Domain as TenantsIcon,
  DateRange as PayPeriodsIcon,
  Approval as ApprovalIcon,
  AccountTree as DepartmentsIcon,
  Notifications as NotificationsIcon,
  AccountBalance as DeductionsIcon,
  Payments as PayrollIcon,
  CloudSync as ZohoIcon,
  HealthAndSafety as BenefitsIcon,
  HowToReg as EnrollmentIcon,
  Gavel as TaxFilingIcon,
  Backup as BackupIcon,
  Timeline as PipelineIcon,
  BeachAccess as TimeOffIcon,
  HelpOutline as HelpIcon,
  LocationOn as LocationIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon,
  Search as SearchIcon,
  Checklist as TasksIcon,
  AssignmentTurnedIn as MyTasksIcon,
} from '@mui/icons-material';
import { ColorModeProvider, useColorMode } from './context/ColorModeContext';
import DashboardHome from './components/Dashboard';
import TimesheetEntries from './components/TimesheetEntries';
import People from './components/People';
import Companies from './components/Companies';
import BillRateMatrix from './components/BillRateMatrix';
import Affiliates from './components/Affiliates';
import EmployeeHoursLookup from './components/EmployeeHoursLookup';
import Invoices from './components/Invoices';
import Expenses from './components/Expenses';
import Login from './components/Login';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import ChangePasswordRequired from './components/ChangePasswordRequired';
import AuditLog from './components/AuditLog';
import Reports from './components/Reports';
import ReportBuilder from './components/ReportBuilder';
import AutomatedReports from './components/AutomatedReports';
import UserProfileDialog from './components/UserProfileDialog';
import TenantManagement from './components/TenantManagement';
import LandingPage from './components/LandingPage';
import SignupPage from './components/SignupPage';
import PricingPage from './components/PricingPage';
import CalendarView from './components/CalendarView';
import ShiftConfiguration from './components/ShiftConfiguration';
import PayPeriodManagement from './components/PayPeriodManagement';
import ApprovalQueue from './components/ApprovalQueue';
import DepartmentSettings from './components/DepartmentSettings';
import NotificationSettings from './components/NotificationSettings';
import SecuritySettings from './components/SecuritySettings';
import HolidayManagement from './components/HolidayManagement';
import DeductionTemplates from './components/DeductionTemplates';
import Payroll from './components/Payroll';
import PaymentPipeline from './components/PaymentPipeline';
import MyPay from './components/MyPay';
import BenefitPlans from './components/BenefitPlans';
import EnrollmentAdmin from './components/EnrollmentAdmin';
import MyBenefits from './components/MyBenefits';
import TaxFilings from './components/TaxFilings';
import TaxDeposits from './components/TaxDeposits';
import YTDImport from './components/YTDImport';
import RoleManagement from './components/RoleManagement';
import CompanySettings from './components/CompanySettings';
import BackupSettings from './components/BackupSettings';
import KioskMode from './components/KioskMode';
import WorkSiteManagement from './components/WorkSiteManagement';
import PTORequests from './components/PTORequests';
import HelpCenter from './components/HelpCenter';
import MenuNavigation from './components/MenuNavigation';
import Sidebar from './components/Sidebar';
import CommandPalette, { isMac } from './components/CommandPalette';
import MFASetup from './components/MFASetup';
import NotificationBell from './components/NotificationBell';
import UserNotificationPreferences from './components/UserNotificationPreferences';
import Tasks from './components/Tasks';
import MyTasks from './components/MyTasks';
import { getPendingApprovalCount, getPendingPTOCount, getCurrentUser } from './api';
import { setToken, removeToken, getToken } from './utils/tenantStorage';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} style={{ display: value !== index ? 'none' : 'block' }}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

// Icon mapping for tabs
const tabIcons = {
  'Dashboard': <DashboardIcon />,
  'Tenants': <TenantsIcon />,
  'Calendar': <CalendarIcon />,
  'My Time Entries': <TimeEntriesIcon />,
  'Timesheets': <HoursIcon />,
  'My Hours': <HoursIcon />,
  'Expenses': <ExpensesIcon />,
  'My Expenses': <ExpensesIcon />,
  'People': <EmployeesIcon />,
  'Clients': <BusinessIcon />,
  'Client Bill Rates': <PayPeriodsIcon />,
  'Affiliates': <AffiliatesIcon />,
  'Departments': <DepartmentsIcon />,
  'Invoices': <InvoicesIcon />,
  'Pay Periods': <PayPeriodsIcon />,
  'Email Schedules': <EmailIcon />,
  'Reports': <ReportsIcon />,
  'Builder': <ReportsIcon />,
  'Automation': <TimeEntriesIcon />,
  'Email Settings': <EmailIcon />,
  'Transaction Log': <AuditIcon />,
  'Shift Config': <ClockIcon />,
  'Approvals': <ApprovalIcon />,
  'Notification Settings': <NotificationsIcon />,
  'Security': <SecurityIcon />,
  'Holidays': <CalendarIcon />,
  'Deductions': <DeductionsIcon />,
  'Payroll': <PayrollIcon />,
  'My Pay': <PayrollIcon />,
  'Zoho': <ZohoIcon />,
  'Benefits': <BenefitsIcon />,
  'Backups': <BackupIcon />,
  'Enrollment': <EnrollmentIcon />,
  'My Benefits': <BenefitsIcon />,
  'Tax Filing': <TaxFilingIcon />,
  'Tax Deposits': <TaxFilingIcon />,
  'YTD Import': <TaxFilingIcon />,
  'Pipeline': <PipelineIcon />,
  'Time Off': <TimeOffIcon />,
  'Company Setup': <BusinessIcon />,
  'Setup Guide': <HelpIcon />,
  'Work Sites': <LocationIcon />,
  'Tasks': <TasksIcon />,
  'My Tasks': <MyTasksIcon />,
};

// Main dashboard component
function Dashboard() {
  const { user: currentUser, logout: onLogout, updateUser, isAdmin, isPayrollAdmin, isManager, isProgramAdmin, isAdminOrManager, hasPermission } = useAuth();
  const { pipeline_enabled: pipelineEnabled, approvalEnabled } = usePipeline();
  // Tenant features from the server feature registry (module switches under
  // Company Setup → General → Modules, plus tasks). Contractors (1099) never
  // get the self-service pages regardless of the switches.
  const { features, loaded: featuresLoaded } = useFeatures();
  const { navLabel } = useTerms();
  const isContractor = currentUser?.employeeType === 'contractor';
  const { mode: colorMode, cycleMode } = useColorMode();
  // Use centralized responsive hook for consistent breakpoints
  const { isMobile, isMobileOrTablet } = useResponsive();
  // isMobile = < 600px (phones), isMobileOrTablet = < 900px (phones + tablets)

  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [pendingPTOCount, setPendingPTOCount] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Tasks is an opt-in tenant feature (FeaturesContext refreshes it when
  // Tasks.js dispatches 'hourtrax:tasks-settings-changed').
  const tasksEnabled = features.tasks;
  const adminLike = isProgramAdmin || isAdmin;

  // Refresh the stored session once per app load per user from /auth/me.
  // - employeeType: a session stored before module gating has no such key
  //   (undefined, not null); fill it so a contractor doesn't keep Time Off /
  //   My Pay / My Benefits until the next login. null = no employee record,
  //   or not a contractor.
  // - timezone / tenantTimezone: sessions stored before the company-timezone
  //   fallback carry a collapsed 'America/New_York' and no tenantTimezone, and
  //   an admin may have changed the company timezone since login.
  const sessionRefreshedFor = React.useRef(null);
  useEffect(() => {
    if (!currentUser?.id || sessionRefreshedFor.current === currentUser.id) return;
    const refreshedId = currentUser.id;
    sessionRefreshedFor.current = refreshedId;
    getCurrentUser()
      .then(
        (res) => {
          // A logout / login as someone else while this was in flight: don't
          // merge the previous user's fields into the new session.
          if (sessionRefreshedFor.current !== refreshedId) return;
          updateUser({
            employeeType: res.data?.employeeType ?? null,
            timezone: res.data?.timezone ?? null,
            tenantTimezone: res.data?.tenantTimezone ?? null,
          });
        },
        () => { if (sessionRefreshedFor.current === refreshedId) sessionRefreshedFor.current = null; /* offline: keep the stored session */ }
      );
  }, [currentUser, updateUser]);

  // ⌘K / Ctrl+K opens the command palette (desktop shell)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(open => !open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Fetch pending approval counts (time entries + PTO)
  // Skip approval count entirely when approval stage is disabled for this tenant
  useEffect(() => {
    const fetchPendingCounts = async () => {
      // Wait for the tenant's real feature flags: the defaults would fire a
      // PTO count at a tenant whose Time off is off.
      if (isAdminOrManager && featuresLoaded) {
        try {
          const approvalCountPromise = approvalEnabled
            ? getPendingApprovalCount().catch(() => ({ data: { count: 0 } }))
            : Promise.resolve({ data: { count: 0 } });
          const [entriesRes, ptoRes] = await Promise.all([
            approvalCountPromise,
            features.time_off
              ? getPendingPTOCount().catch(() => ({ data: { count: 0 } }))
              : Promise.resolve({ data: { count: 0 } }),
          ]);
          setPendingApprovalCount(entriesRes.data.count || 0);
          setPendingPTOCount(ptoRes.data.count || 0);
        } catch (err) {
          console.error('Failed to fetch pending counts:', err);
        }
      }
    };
    fetchPendingCounts();
    // Refresh count every 60 seconds
    const interval = setInterval(fetchPendingCounts, 60000);
    return () => clearInterval(interval);
  }, [isAdminOrManager, approvalEnabled, featuresLoaded, features.time_off]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Ref used to preserve selected tab across pipeline config changes
  const lastTabLabelRef = React.useRef(null);

  // Rendered (pipeline-filtered) tabs, kept in a ref so the window-event
  // listener below always resolves indices against what is actually shown.
  // Resolving against the unfiltered list mis-navigated by one slot per
  // hidden tab (e.g. "Invoices" landed on Pay Periods when the pipeline
  // stage tabs were disabled for the tenant).
  const tabsRef = React.useRef([]);

  // Listen for tab navigation events from child components (e.g. Create Invoice from Timesheets)
  useEffect(() => {
    const handler = (e) => {
      const targetLabel = e.detail?.tab;
      if (targetLabel) {
        const idx = tabsRef.current.findIndex(t => t.label === targetLabel);
        if (idx >= 0) setTabValue(idx);
      }
    };
    window.addEventListener('hourtrax-navigate-tab', handler);
    return () => window.removeEventListener('hourtrax-navigate-tab', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get approval icon with badge
  const getApprovalIcon = () => (
    <Badge badgeContent={pendingApprovalCount} color="error" max={99}>
      {tabIcons['Approvals']}
    </Badge>
  );

  // Get Time Off icon with pending PTO badge (only shows for approvers)
  const getTimeOffIcon = () => (
    <Badge badgeContent={pendingPTOCount} color="error" max={99}>
      {tabIcons['Time Off']}
    </Badge>
  );

  // Single tab registry for every role. `show` gates visibility:
  // adminLike (program admin/admin) and payroll admin get fixed sets,
  // managers are permission-driven, everyone else gets the employee set.
  // Replaces the four near-duplicate per-role arrays this grew from.
  const getTabs = () => {
    const isEmployee = !adminLike && !isPayrollAdmin && !isManager;
    const mgr = (perm) => isManager && hasPermission(perm);

    const defs = [
      { label: 'Dashboard', component: <DashboardHome />, show: true },
      { label: 'Tenants', component: <TenantManagement />, show: isProgramAdmin },
      { label: 'Approvals', component: <ApprovalQueue />, icon: getApprovalIcon(), show: adminLike || isPayrollAdmin || mgr('can_approve_time') || mgr('can_approve_pto') || mgr('can_approve_expenses') },
      { label: 'Calendar', component: <CalendarView />, show: true },
      { label: 'Time Off', component: <PTORequests />, icon: getTimeOffIcon(), show: true },
      { label: 'My Time Entries', component: <TimesheetEntries />, show: true },
      { label: 'Timesheets', component: <EmployeeHoursLookup />, show: !isEmployee },
      { label: 'Tasks', component: <Tasks />, show: adminLike || isPayrollAdmin || mgr('can_manage_tasks') },
      { label: 'My Tasks', component: <MyTasks />, show: Boolean(currentUser?.employeeId) },
      { label: 'Reports', component: <Reports />, show: adminLike || isPayrollAdmin || mgr('can_run_reports') },
      { label: 'Builder', component: <ReportBuilder />, show: adminLike || isPayrollAdmin || mgr('can_run_reports') },
      { label: 'Automation', component: <AutomatedReports />, show: adminLike || isPayrollAdmin || mgr('can_run_reports') },
      { label: 'Expenses', component: <Expenses />, show: adminLike || isPayrollAdmin || mgr('can_approve_expenses') },
      { label: 'My Expenses', component: <Expenses />, show: isEmployee },
      { label: 'Invoices', component: <Invoices />, show: adminLike || isPayrollAdmin || mgr('can_manage_invoices') },
      { label: 'Pipeline', component: <PaymentPipeline />, show: adminLike || isPayrollAdmin || mgr('can_manage_invoices') },
      { label: 'Pay Periods', component: <PayPeriodManagement />, show: adminLike || isPayrollAdmin || mgr('can_manage_invoices') },
      { label: 'People', component: <People />, show: !isEmployee },
      { label: 'Clients', component: <Companies />, show: adminLike || isPayrollAdmin || mgr('can_manage_companies') },
      { label: 'Client Bill Rates', component: <BillRateMatrix />, show: adminLike || isPayrollAdmin || mgr('can_manage_companies') },
      { label: 'Affiliates', component: <Affiliates />, show: adminLike || isPayrollAdmin || mgr('can_manage_companies') },
      { label: 'Departments', component: <DepartmentSettings />, show: adminLike || isPayrollAdmin || mgr('can_configure_settings') },
      { label: 'Deductions', component: <DeductionTemplates />, show: adminLike || isPayrollAdmin || mgr('can_run_payroll') || mgr('can_manage_deductions') },
      { label: 'Payroll', component: <Payroll />, show: adminLike || isPayrollAdmin || mgr('can_run_payroll') },
      { label: 'Benefits', component: <BenefitPlans />, show: adminLike || isPayrollAdmin || mgr('can_manage_benefits') },
      { label: 'Enrollment', component: <EnrollmentAdmin />, show: adminLike || isPayrollAdmin || mgr('can_manage_benefits') },
      // Employer-wide wages: same rule as the server (endpointAccess.canAccessTenantTaxForms, plan 122).
      { label: 'Tax Filing', component: <TaxFilings />, show: canAccessTenantTaxForms(currentUser) },
      { label: 'Tax Deposits', component: <TaxDeposits />, show: canAccessTenantTaxForms(currentUser) },
      { label: 'YTD Import', component: <YTDImport />, show: adminLike || isPayrollAdmin },
      { label: 'My Pay', component: <MyPay />, show: true },
      { label: 'My Benefits', component: <MyBenefits />, show: true },
      { label: 'Notification Settings', component: <NotificationSettings />, show: adminLike || isPayrollAdmin || mgr('can_configure_settings') },
      { label: 'Security', component: <SecuritySettings />, show: adminLike },
      { label: 'Shift Config', component: <ShiftConfiguration />, show: adminLike || mgr('can_configure_settings') },
      { label: 'Holidays', component: <HolidayManagement />, show: adminLike || isPayrollAdmin || mgr('can_configure_settings') },
      { label: 'Company Setup', component: <CompanySettings />, show: adminLike || isPayrollAdmin },
      // Help Center: everyone gets the permission-filtered how-to guides;
      // admins/payroll admins additionally get a Setup Guide sub-tab.
      { label: 'Help', component: <HelpCenter />, icon: tabIcons['Setup Guide'], show: true },
      { label: 'Backups', component: <BackupSettings />, show: adminLike },
      { label: 'Transaction Log', component: <AuditLog />, show: isProgramAdmin || hasPermission('can_view_audit_log') },
      { label: 'Roles', component: <RoleManagement />, icon: tabIcons['People'], show: adminLike },
      { label: 'Work Sites', component: <WorkSiteManagement />, show: adminLike || isPayrollAdmin || mgr('can_manage_work_sites') },
    ];

    return defs
      .filter(d => d.show)
      .map(({ label, component, icon }) => ({ label, component, icon: icon || tabIcons[label] }));
  };

  // Filter tabs by tenant config — pipeline stages, plus the tasks feature and
  // the module switches (Benefits / Time off / Payroll), whose keys come from
  // the server feature registry (useFeatures()). Invoices tab stays regardless
  // (invoicing is independent — auto-invoice and Zoho are useful even without the
  // pipeline's stage gating). Only the Pipeline page itself and approval-related
  // UI depend on pipeline config. The self-service pages (Time Off, My Pay,
  // My Benefits) are also hidden for contractors (1099) — paid outside payroll,
  // no PTO, no benefits. The permission gates in getTabs() still apply on top.
  // Sidebar and the mobile MenuNavigation both derive from the returned list
  // (navConfig filters its groups by the available labels), so nothing here
  // needs repeating there.
  const filterTabsByConfig = (rawTabs) => rawTabs.filter((t) => {
    if (t.label === 'Approvals') return approvalEnabled;
    if (t.label === 'Pipeline') return pipelineEnabled;
    // My Tasks only makes sense once the feature is on. Tasks stays visible
    // to admins even while off, so they have somewhere to find the toggle.
    if (t.label === 'My Tasks') return tasksEnabled;
    if (t.label === 'Tasks') return tasksEnabled || adminLike;
    if (t.label === 'Time Off') return features.time_off && !isContractor;
    if (t.label === 'My Benefits') return features.benefits && !isContractor;
    // Payroll, Tax Filing and My Pay stay (read-only) while Payroll is off if
    // the tenant has payroll history; Deductions / YTD Import need Payroll on.
    const payrollRule = payrollTabVisible(t.label, features, { isContractor });
    if (payrollRule !== undefined) return payrollRule;
    if (t.label === 'Benefits' || t.label === 'Enrollment') return features.benefits;
    return true;
  });
  const tabs = filterTabsByConfig(getTabs());
  tabsRef.current = tabs;

  // Preserve selected tab by label when pipeline or module config adds/removes
  // tabs, so the user doesn't get dumped onto a different page (an admin
  // flipping Payroll off from Company Setup stays on Company Setup).
  useEffect(() => {
    const currentLabel = tabs[tabValue]?.label;
    if (currentLabel) lastTabLabelRef.current = currentLabel;
  }, [tabValue]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const desiredLabel = lastTabLabelRef.current;
    if (!desiredLabel) return;
    const currentLabel = tabs[tabValue]?.label;
    if (currentLabel !== desiredLabel) {
      const newIdx = tabs.findIndex(t => t.label === desiredLabel);
      // The tab we were on may have been removed (module switched off,
      // contractor type hydrated mid-session): fall back to the first tab
      // rather than leaving tabValue pointing past the array at a blank pane.
      if (newIdx >= 0) {
        if (newIdx !== tabValue) setTabValue(newIdx);
      } else if (tabValue >= tabs.length || !tabs[tabValue]) {
        setTabValue(0);
      }
    }
  }, [pipelineEnabled, approvalEnabled, features.benefits, features.time_off, features.payroll, features.payroll_history, features.tasks, isContractor]); // eslint-disable-line react-hooks/exhaustive-deps
  const navigateToTab = (tabLabel) => {
    const idx = tabs.findIndex(t => t.label === tabLabel);
    if (idx >= 0) setTabValue(idx);
  };

  const roleLabel = isProgramAdmin ? 'Program Admin' : isAdmin ? 'Admin' : isPayrollAdmin ? 'Payroll Admin' : isManager ? 'Manager' : 'Employee';

  // Skip link for keyboard navigation (both layouts)
  const skipLink = (
    <a href="#main-content" style={{
      position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden',
      zIndex: 10000, padding: '8px 16px', background: '#0D9488', color: '#fff', textDecoration: 'none',
    }} onFocus={(e) => { e.target.style.left = '8px'; e.target.style.width = 'auto'; e.target.style.height = 'auto'; }}
       onBlur={(e) => { e.target.style.left = '-9999px'; e.target.style.width = '1px'; e.target.style.height = '1px'; }}>
      Skip to main content
    </a>
  );

  const userMenu = (
    <Menu
      id="user-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
      sx={{ zIndex: 9999 }}
    >
      <MenuItem disabled>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {currentUser.firstName || currentUser.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentUser.username}
          </Typography>
          {isMobile && (
            <Chip
              label={roleLabel}
              color={isAdmin ? 'error' : isPayrollAdmin ? 'info' : isManager ? 'warning' : 'default'}
              size="small"
              sx={{ mt: 0.5, display: 'block' }}
            />
          )}
        </Box>
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => { setShowProfile(true); setAnchorEl(null); }}>
        <ProfileIcon sx={{ mr: 1 }} fontSize="small" />
        My Profile
      </MenuItem>
      <MenuItem onClick={() => { setShowChangePassword(true); setAnchorEl(null); }}>
        <ChangePasswordIcon sx={{ mr: 1 }} fontSize="small" />
        Change Password
      </MenuItem>
      <MenuItem onClick={() => { setShowMfaSetup(true); setAnchorEl(null); }}>
        <SecurityIcon sx={{ mr: 1 }} fontSize="small" />
        Two-Factor Auth
      </MenuItem>
      <MenuItem onClick={() => { setShowNotifPrefs(true); setAnchorEl(null); }}>
        <NotificationsIcon sx={{ mr: 1 }} fontSize="small" />
        Notification Preferences
      </MenuItem>
      {/* Cycles System → Light → Dark. System follows the OS preference. */}
      <MenuItem onClick={cycleMode}>
        {colorMode === 'light' ? <LightModeIcon sx={{ mr: 1 }} fontSize="small" />
          : colorMode === 'dark' ? <DarkModeIcon sx={{ mr: 1 }} fontSize="small" />
          : <SystemModeIcon sx={{ mr: 1 }} fontSize="small" />}
        Theme: {colorMode === 'system' ? 'System' : colorMode === 'dark' ? 'Dark' : 'Light'}
      </MenuItem>
      <Divider />
      <MenuItem onClick={onLogout}>
        <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
        Logout
      </MenuItem>
    </Menu>
  );

  const userMenuButton = (
    <IconButton
      color="inherit"
      aria-label="User menu"
      aria-controls="user-menu"
      aria-haspopup="true"
      onClick={(e) => setAnchorEl(e.currentTarget)}
    >
      <AccountCircle />
    </IconButton>
  );

  const notificationBell = (
    <NotificationBell onNavigate={(tabLabel) => {
      const idx = tabs.findIndex(t => t.label === tabLabel);
      if (idx >= 0) setTabValue(idx);
    }} />
  );

  const tabPanels = tabs.map((tab, index) => (
    <TabPanel key={index} value={tabValue} index={index}>
      {tab.component && tab.component.type === EmployeeHoursLookup
        ? React.cloneElement(tab.component, { onNavigate: navigateToTab })
        : tab.component}
    </TabPanel>
  ));

  const versionFooter = (
    <Box sx={{ textAlign: 'center', py: 1, opacity: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        HourTrax v{process.env.REACT_APP_VERSION || 'dev'}
        {process.env.REACT_APP_GIT_COMMIT && process.env.REACT_APP_GIT_COMMIT !== 'unknown'
          ? ` (${process.env.REACT_APP_GIT_COMMIT.substring(0, 7)})`
          : ''}
      </Typography>
    </Box>
  );

  const dialogs = (
    <>
      <ChangePasswordDialog
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
      <UserProfileDialog
        open={showProfile}
        onClose={() => setShowProfile(false)}
        user={currentUser}
        onProfileUpdated={(updates) => updateUser(updates)}
      />
      <MFASetup
        open={showMfaSetup}
        onClose={() => setShowMfaSetup(false)}
      />
      <UserNotificationPreferences
        open={showNotifPrefs}
        onClose={() => setShowNotifPrefs(false)}
      />
    </>
  );

  // Never render a tab list from default flags — a Payroll-off tenant would
  // briefly get Payroll tabs. Hooks above always run; only the render waits.
  if (!featuresLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }

  // ── Desktop: persistent sidebar + slim top bar ──────────────────────
  if (!isMobileOrTablet) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'stretch' }}>
        {skipLink}
        <Sidebar
          tabs={tabs}
          currentTab={tabValue}
          onNavigate={(idx) => setTabValue(idx)}
          roleFlags={{ isProgramAdmin, isAdmin, isPayrollAdmin, isManager }}
          roleLabel={roleLabel}
          tenantName={currentUser?.tenantName}
          user={currentUser}
          pendingApprovalCount={pendingApprovalCount}
          pendingPTOCount={pendingPTOCount}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            component="header"
            sx={{
              position: 'sticky', top: 0, zIndex: 5,
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 3, py: 1.25,
              borderBottom: 1, borderColor: 'divider',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(13,20,19,0.82)' : 'rgba(246,248,248,0.82)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
              {navLabel(tabs[tabValue]?.label || '')}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* Global search — opens the ⌘K command palette */}
            <ButtonBase
              onClick={() => setPaletteOpen(true)}
              aria-label="Search"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                borderRadius: '8px', px: 1.25, py: 0.75, width: 270,
                color: 'text.faint', fontSize: '0.8125rem', fontFamily: 'inherit',
                justifyContent: 'flex-start',
                '&:hover': { borderColor: 'borderStrong' },
              }}
            >
              <SearchIcon sx={{ fontSize: 15 }} />
              <Box component="span" sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', whiteSpace: 'nowrap' }}>Search…</Box>
              <Box component="kbd" sx={{ fontSize: '0.6875rem', border: 1, borderColor: 'divider', borderRadius: '4px', px: 0.75, py: 0.125, bgcolor: 'background.surface2', fontFamily: 'inherit' }}>
                {isMac ? '⌘K' : 'Ctrl K'}
              </Box>
            </ButtonBase>
            {notificationBell}
            {userMenuButton}
          </Box>
          <Container
            id="main-content"
            component="main"
            maxWidth={false}
            sx={{ mt: 2, px: 3, flex: 1 }}
          >
            {tabPanels}
          </Container>
          {versionFooter}
        </Box>
        {userMenu}
        {dialogs}
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          tabs={tabs}
          onNavigate={navigateToTab}
          canSearchData={canUseGlobalSearch(currentUser)}
        />
      </Box>
    );
  }

  // ── Mobile / tablet: top app bar + bottom tab nav (unchanged) ───────
  return (
    <Box sx={{ pb: 7 }}>
      {skipLink}
      <AppBar position="fixed" component="nav" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          {/* Logo and Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <ClockIcon sx={{ mr: 1 }} />
            <Typography
              variant={isMobile ? "body1" : "h6"}
              component="div"
              sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              HourTrax™
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && (
            <Chip
              label={roleLabel}
              size="small"
              sx={{
                mr: 1,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                bgcolor: isAdmin ? 'rgba(239,68,68,0.3)' : isPayrollAdmin ? 'rgba(59,130,246,0.3)' : isManager ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.1)',
              }}
              variant="outlined"
            />
          )}

          {notificationBell}
          {userMenuButton}
          {userMenu}
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed AppBar */}
      <Toolbar />

      {/* Main Content */}
      <Container
        id="main-content"
        component="main"
        maxWidth={false}
        sx={{
          mt: isMobile ? 1 : 2,
          px: { xs: 1, sm: 2 }
        }}
      >
        {tabPanels}
      </Container>

      {/* Bottom tab navigation + swipe drawer */}
      <MenuNavigation
        tabs={tabs}
        currentTab={tabValue}
        onTabChange={handleTabChange}
        pendingApprovalCount={pendingApprovalCount}
        pendingPTOCount={pendingPTOCount}
        isProgramAdmin={isProgramAdmin}
        isAdmin={isAdmin}
        isPayrollAdmin={isPayrollAdmin}
        isManager={isManager}
      />

      {dialogs}
    </Box>
  );
}

// Tenant-specific route handler
// OIDC callback handler — extracts token from URL hash after Entra ID redirect
function OIDCCallback() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const hash = window.location.hash.substring(1); // remove #
    const params = new URLSearchParams(hash);
    const token = params.get('token');

    if (!token) {
      setError('Authentication failed. No token received.');
      return;
    }

    // Store token first so API calls are authenticated, then fetch server-verified user info
    setToken(token, tenantSlug);

    getCurrentUser()
      .then((response) => {
        const me = response.data;
        const userData = {
          id: me.id,
          username: me.username,
          role: me.role,
          roleId: me.roleId ?? null,
          roleSlug: me.roleSlug ?? me.role,
          roleName: me.roleName ?? me.role,
          employeeId: me.employeeId,
          tenantId: me.tenantId,
          tenantSlug: me.tenantSlug || tenantSlug,
          tenantName: me.tenantName,
          firstName: me.firstName,
          lastName: me.lastName,
          permissions: me.permissions,
          employeeType: me.employeeType ?? null,
          timezone: me.timezone ?? null,
          tenantTimezone: me.tenantTimezone ?? null,
        };
        login(userData, token);
        navigate(`/${tenantSlug}`, { replace: true });
      })
      .catch((err) => {
        console.error('OIDC callback error:', err);
        removeToken(tenantSlug);
        setError('Failed to process authentication response.');
      });
  }, [tenantSlug, login, navigate]);

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Card sx={{ maxWidth: 400, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>Sign-in Failed</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(`/${tenantSlug}`)}>
            Try Again
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );
}

function TenantRoute() {
  const { tenantSlug } = useParams();
  const { user, login, updateUser } = useAuth();

  // Installing the app from a tenant URL should reopen at that tenant. The
  // static manifest's start_url is "/", so Android launched every pinned icon
  // at hourtrax.com and the user landed on tenant selection instead of their
  // own login.
  useEffect(() => {
    if (!tenantSlug) return;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute('href');
    link.setAttribute('href', `/api/manifest/tenant/${encodeURIComponent(tenantSlug)}`);
    return () => { if (original) link.setAttribute('href', original); };
  }, [tenantSlug]);

  // Check if logged-in user belongs to this tenant (or is program_admin)
  const validForTenant = user && (user.tenantSlug === tenantSlug || user.role === 'program_admin');

  const handleLogin = (userData) => {
    const token = getToken(tenantSlug);
    login(userData, token);
  };

  if (!validForTenant) {
    // Show login for this tenant — preserve existing session so user can navigate back
    return <Login onLogin={handleLogin} tenantSlug={tenantSlug} isAdminLogin={false} />;
  }

  // Handle forced password change
  if (user.mustChangePassword) {
    return (
      <ChangePasswordRequired
        onPasswordChanged={() => updateUser({ mustChangePassword: false })}
      />
    );
  }

  return <Dashboard />;
}

// Admin route handler
function AdminRoute() {
  const navigate = useNavigate();
  const { user, login, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    const token = getToken(userData.tenantSlug || 'admin');
    login(userData, token);
    // If user logged into a specific tenant, redirect to tenant URL
    if (userData.tenantSlug && userData.role !== 'program_admin') {
      navigate(`/${userData.tenantSlug}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} isAdminLogin={true} />;
  }

  // Handle forced password change
  if (user.mustChangePassword) {
    return (
      <ChangePasswordRequired
        onPasswordChanged={() => updateUser({ mustChangePassword: false })}
      />
    );
  }

  return <Dashboard />;
}

function App() {
  return (
    <ColorModeProvider>
      <Routes>
        {/* Real landing page at the root; /preview kept as a legacy alias */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/preview" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/admin" element={<AdminRoute />} />
        <Route path="/:tenantSlug/auth/callback" element={<OIDCCallback />} />
        <Route path="/:tenantSlug/kiosk/:kioskCode" element={<KioskMode />} />
        <Route path="/:tenantSlug/*" element={<TenantRoute />} />
      </Routes>
    </ColorModeProvider>
  );
}

export default App;
